import React, { useState } from "react";
import { Play } from "lucide-react";
import { track } from "../lib/checkout";

const VIDEOS = [
  {
    id: "1181596295",
    name: "#1 Business Expert",
    tag: "Authority Growth",
    thumb:
      "https://i.vimeocdn.com/video/2143998277-cea5740ab367eed3ed47c54ada2b085f3f48645dd3eec0bf1f5ba8491e381446-d?mw=900&q=85",
  },
  {
    id: "1181596181",
    name: "#1 Health Expert",
    tag: "Content Performance",
    thumb:
      "https://i.vimeocdn.com/video/2143998128-7f1ef1d2733bff9658d304d54267e429cf3529a827cbd580aa57a4eccd0c5444-d?mw=900&q=85",
  },
  {
    id: "1194618557",
    name: "Skin Expert",
    tag: "Lead Generation",
    thumb:
      "https://i.vimeocdn.com/video/2160313519-bd766f6178bc6a0328f260bf31f96082e70ac5b341fd25e7e7440ae268ab34ea-d?mw=900&q=85",
  },
  {
    id: "1194618559",
    name: "Yoga Expert",
    tag: "Business Growth",
    thumb:
      "https://i.vimeocdn.com/video/2160313509-0d0c44e93c8ec6306eb1f43c99009fa36bcf846fcec7171e64e6b077f60cbcac-d?mw=900&q=85",
  },
  {
    id: "1194618558",
    name: "Performance Expert",
    tag: "Implementation",
    thumb:
      "https://i.vimeocdn.com/video/2160313455-753796cc404ad9326fb3011e278b5885f4a30922e722ab0ef1f732bccdde7d0d-d?mw=900&q=85",
  },
  {
    id: "1199714154",
    name: "Corporate Health Expert",
    tag: "Business Growth",
    thumb:
      "https://i.vimeocdn.com/video/2166611629-7cec5d417a0700306a902af20a81eefa8ff85367d84d6aca386a0e467bb0f67a-d?mw=900&q=85",
  },
  {
    id: "1199714006",
    name: "PCOD Expert",
    tag: "Lead Generation",
    thumb:
      "https://i.vimeocdn.com/video/2166611562-36621957e5c5f632a146a9fbe45ce47e353f03d5df9da2567a1fb2767e1e2664-d?mw=900&q=85",
  },
  {
    id: "1199713180",
    name: "Life Coach Expert",
    tag: "Lead Generation",
    thumb:
      "https://i.vimeocdn.com/video/2166610168-a0ec9760dc5c30ba7696040d78b34758259aef713674548544c91395feaf3be2-d?mw=900&q=85",
  },
];

function vimeoUrl(id) {
  // Click-to-play: unmuted, with controls, starts at t=0
  return `https://player.vimeo.com/video/${id}?autoplay=1&muted=0&controls=1&playsinline=1&dnt=1#t=0s`;
}

function VideoCard({ video, isPlaying, onPlay }) {
  return (
    <div className="lime-img-card aspect-[9/16] relative group overflow-hidden bg-[#0a0f0a]">
      {isPlaying ? (
        <iframe
          src={vimeoUrl(video.id)}
          className="absolute inset-0 w-full h-full"
          title={`${video.name} — ${video.tag}`}
          allow="autoplay; fullscreen; picture-in-picture"
          frameBorder="0"
        />
      ) : (
        <>
          {/* Thumbnail as the primary image */}
          <img
            src={video.thumb}
            alt={`${video.name} — ${video.tag}`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />

          {/* Dim overlay + translucent play button */}
          <button
            type="button"
            onClick={onPlay}
            className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/25 group-hover:bg-black/15 transition cursor-pointer"
            aria-label={`Play testimonial from ${video.name}`}
          >
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/15 backdrop-blur-md border border-white/40 flex items-center justify-center shadow-[0_0_30px_rgba(0,0,0,0.4)] group-hover:bg-[#C4FF3D]/85 group-hover:border-[#C4FF3D] group-hover:scale-110 transition-all duration-300">
              <Play
                className="w-6 h-6 md:w-8 md:h-8 text-white group-hover:text-[#0a0f0a] ml-1 transition-colors"
                fill="currentColor"
              />
            </span>
          </button>
        </>
      )}

      {/* Top tag chip */}
      <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2 z-10 pointer-events-none">
        <span className="px-2 py-1 rounded-md bg-[#0a0f0a]/85 border border-[rgba(196,255,61,0.35)] text-[#C4FF3D] text-[10px] uppercase tracking-wider font-bold backdrop-blur-sm">
          {video.tag}
        </span>
      </div>

      {/* Bottom name chip */}
      <div className="absolute bottom-2 left-2 right-2 z-10 pointer-events-none">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#0a0f0a]/85 border border-[rgba(196,255,61,0.25)] text-white text-xs md:text-sm font-semibold backdrop-blur-sm">
          {video.name}
        </span>
      </div>
    </div>
  );
}

export default function SocialProof() {
  // Only one video plays at a time
  const [playingId, setPlayingId] = useState(null);

  const handlePlay = (video) => {
    setPlayingId(video.id);
    track("VideoPlay", { source: "social_proof", video_id: video.id, name: video.name });
  };

  return (
    <section className="relative z-10 px-4 py-10 md:py-14">
      <div className="max-w-7xl mx-auto">
        <p className="text-center text-[#C4FF3D] uppercase tracking-[0.28em] text-[11px] md:text-xs font-bold mb-3">
          Voices From The Room
        </p>
        <h2 className="display-font text-center text-3xl md:text-5xl font-extrabold leading-tight mb-4">
          <span className="text-white">The Experts Who Chose to</span>{" "}
          <span className="lime-box">Build Differently.</span>
        </h2>
        <p className="text-center text-white/70 max-w-2xl mx-auto mb-10 text-base md:text-lg">
          See what&apos;s possible when the system does the heavy lifting.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {VIDEOS.map((v) => (
            <VideoCard
              key={v.id}
              video={v}
              isPlaying={playingId === v.id}
              onPlay={() => handlePlay(v)}
            />
          ))}
        </div>

        <p className="text-white/40 text-[11px] md:text-xs mt-8 text-center italic max-w-2xl mx-auto leading-relaxed">
          Disclaimer: Testimonials reflect individual experiences. Any references to revenue, followers or client outcomes are specific to those individuals and are not a guarantee of your results.
        </p>
      </div>
    </section>
  );
}
